import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FiChevronDown, FiSliders, FiX } from "react-icons/fi";
import "../css/shop.scss";
import productData from "../Datas/productData";
import ProductCard from "./ProductCard";

const categories = ["All", "Jeans", "Shorts & Skirts", "Tops", "Dresses & Jumpsuits", "Jackets"];
const fits = ["All fits", ...new Set(productData.map((product) => product.fit))];

function Shop() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const [filterOpen, setFilterOpen] = useState(false);
  const [sort, setSort] = useState(params.get("sort") || "featured");

  const selectedCategory = params.get("category") || "All";
  const selectedFit = params.get("fit") || "All fits";
  const query = (params.get("q") || "").toLowerCase();
  const saleOnly = params.get("sale") === "true";

  useEffect(() => {
    setSort(params.get("sort") || "featured");
  }, [params]);

  const updateParam = (key, value, allValue) => {
    const next = new URLSearchParams(location.search);
    if (!value || value === allValue) next.delete(key);
    else next.set(key, value);
    navigate(`/shop${next.toString() ? `?${next}` : ""}`);
  };

  const products = useMemo(() => {
    const filtered = productData.filter((product) => {
      const categoryMatch = selectedCategory === "All" || product.category === selectedCategory;
      const fitMatch = selectedFit === "All fits" || product.fit === selectedFit;
      const queryMatch = !query || `${product.name} ${product.category} ${product.colors.join(" ")}`.toLowerCase().includes(query);
      const saleMatch = !saleOnly || Boolean(product.compareAtPrice);
      return categoryMatch && fitMatch && queryMatch && saleMatch;
    });

    return [...filtered].sort((a, b) => {
      if (sort === "price-low") return Number(a.price) - Number(b.price);
      if (sort === "price-high") return Number(b.price) - Number(a.price);
      if (sort === "name") return a.name.localeCompare(b.name);
      if (sort === "newest") return Number(Boolean(b.badge === "NEW")) - Number(Boolean(a.badge === "NEW"));
      return a.id - b.id;
    });
  }, [query, saleOnly, selectedCategory, selectedFit, sort]);

  const clearFilters = () => {
    setSort("featured");
    navigate("/shop");
  };

  return (
    <main className="shop-page">
      <header className="shop-heading">
        <p className="eyebrow">The complete collection</p>
        <h1>{saleOnly ? "Final Sale" : selectedCategory === "All" ? "Shop All" : selectedCategory}</h1>
        {query && <p>Results for “{params.get("q")}”</p>}
      </header>

      <nav className="category-tabs" aria-label="Product categories">
        {categories.map((category) => (
          <button key={category} className={selectedCategory === category ? "active" : ""} onClick={() => updateParam("category", category, "All")}>{category}</button>
        ))}
      </nav>

      <div className="collection-toolbar">
        <button className="filter-trigger" type="button" onClick={() => setFilterOpen(true)}><FiSliders /> Filter & sort</button>
        <span>{products.length} products</span>
        <label className="sort-select">Sort by <select value={sort} onChange={(event) => setSort(event.target.value)}>
          <option value="featured">Featured</option><option value="newest">Newest</option><option value="price-low">Price: low to high</option><option value="price-high">Price: high to low</option><option value="name">Name</option>
        </select><FiChevronDown aria-hidden="true" /></label>
      </div>

      {products.length ? (
        <div className="product-grid">
          {products.map((product, index) => <ProductCard product={product} key={product.id} priority={index < 4} />)}
        </div>
      ) : (
        <div className="no-results"><h2>No styles found</h2><p>Try a different category, fit or search.</p><button className="button button--dark" onClick={clearFilters}>Reset filters</button></div>
      )}

      <aside className={`filter-drawer ${filterOpen ? "is-visible" : ""}`} aria-hidden={!filterOpen}>
        <div className="filter-drawer__head"><strong>Filter & sort</strong><button type="button" onClick={() => setFilterOpen(false)} aria-label="Close filters"><FiX /></button></div>
        <fieldset><legend>Category</legend>{categories.map((category) => <label key={category}><input type="radio" name="category" checked={selectedCategory === category} onChange={() => updateParam("category", category, "All")} />{category}</label>)}</fieldset>
        <fieldset><legend>Fit</legend>{fits.map((fit) => <label key={fit}><input type="radio" name="fit" checked={selectedFit === fit} onChange={() => updateParam("fit", fit, "All fits")} />{fit}</label>)}</fieldset>
        <div className="filter-drawer__actions"><button type="button" onClick={clearFilters}>Reset</button><button className="button button--dark" type="button" onClick={() => setFilterOpen(false)}>Show {products.length} products</button></div>
      </aside>
      {filterOpen && <button className="filter-scrim" aria-label="Close filters" onClick={() => setFilterOpen(false)} />}
    </main>
  );
}

export default Shop;
